import styled from '@emotion/native';
import {
  ClinicalImpressionAssessmentValueCode,
  ClinicalImpressionCode,
  ClinicalImpressionLocationCode,
  ClinicalImpressionPlanValueCode,
  getReferenceId,
} from '@quromedical/fhir-common';
import {
  MeasurementName,
  type VitalsLatestResultMeasures,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { Col, Row } from 'components/base';
import type { Field, SelectOption } from 'components/types';
import { getStatusMeasurements } from 'core/vitals';
import { Button, type FileKeyProgressMap, Visible } from 'design-system';
import { StatusGrid } from 'design-system/components';
import { PADDING } from 'design-system/theme';
import type { Reference } from 'fhir/r4';
import type React from 'react';
import { strings } from 'strings';
import type { ObjectType } from 'validation';
import * as yup from 'yup';

import {
  assessmentOptions,
  codeOptions,
  locationOptions,
  planOptions,
} from './mapping';
import type { FormData } from './types';

const StatusRow = styled(Row)({
  paddingLeft: PADDING['3xs'],
});

interface StatusDisplayProps {
  latest: VitalsLatestResultMeasures;
  onAdd: () => void;
  showAdd: boolean;
}

export const StatusDisplay: React.FC<StatusDisplayProps> = ({
  onAdd: handleAdd,
  showAdd = false,
  latest,
}) => {
  const measurements = getStatusMeasurements({
    latest,
    additionalMeasures: [MeasurementName.Posture],
  });

  return (
    <Row alignItems="center">
      <StatusRow flexGrow={1}>
        <StatusGrid lg={4} xl={6} sm={2} measurements={measurements} />
      </StatusRow>
      <Visible if={showAdd}>
        <Col flexGrow={0} flexShrink={0}>
          <Button variant="text" icon="add" onPress={handleAdd} />
        </Col>
      </Visible>
    </Row>
  );
};

interface CreateFieldProps {
  latest: VitalsLatestResultMeasures;
  showObjectiveNote: boolean;
  showAssessmentNote: boolean;
  showPlanNote: boolean;
  handleObjectiveAdd: () => void;
  handleAssessmentAdd: () => void;
  handlePlanAdd: () => void;
  generalPractitioner?: Reference[];
  progress?: FileKeyProgressMap;
  uploadInProgress?: boolean;
  admissionStatus?: string;
}

export const createFields = ({
  latest,
  showObjectiveNote,
  showAssessmentNote,
  showPlanNote,
  handleObjectiveAdd,
  handleAssessmentAdd,
  handlePlanAdd,
  generalPractitioner = [],
  progress = {},
  uploadInProgress = false,
  admissionStatus,
}: CreateFieldProps): Field<FormData>[] => {
  const practitionerOptions: SelectOption[] = generalPractitioner.map(
    (practitioner) => ({
      display:
        practitioner.display || strings.PlaceholderPractitionerDisplayMissing,
      value: getReferenceId(practitioner) as string,
    }),
  );

  const isPlannedAdmission = admissionStatus === 'planned';

  // Filter location options based on admission status
  const availableLocationOptions = isPlannedAdmission
    ? locationOptions.filter(
        (option) => option.value === ClinicalImpressionLocationCode.Virtual,
      )
    : locationOptions;

  // Filter code options based on admission status - only allow pre-onboarding for planned admissions
  // exclude ClinicalImpressionCode.PreOnboarding if its not a planned admission
  const availableCodeOptions = isPlannedAdmission
    ? codeOptions.filter(
        (option) => option.value === ClinicalImpressionCode.PreOnboarding,
      )
    : codeOptions.filter(
        (option) => option.value !== ClinicalImpressionCode.PreOnboarding,
      );

  // Filter assessment options based on admission status - only allow stable and deteriorating for planned admissions
  const availableAssessmentOptions = isPlannedAdmission
    ? assessmentOptions.filter(
        (option) =>
          option.value === ClinicalImpressionAssessmentValueCode.Stable ||
          option.value === ClinicalImpressionAssessmentValueCode.Deteriorating,
      )
    : assessmentOptions;

  // Filter planOptions based on admission status - only allow continue onboarding, escalate and decline for planned admissions
  const availablePlanOptions = isPlannedAdmission
    ? planOptions
        .filter(
          (option) =>
            option.value === ClinicalImpressionPlanValueCode.Continue ||
            option.value === ClinicalImpressionPlanValueCode.Escalate ||
            option.value === ClinicalImpressionPlanValueCode.DeclinedOnboarding,
        )
        .map((option) =>
          option.value === ClinicalImpressionPlanValueCode.Continue
            ? { ...option, display: strings.SOAPNotePlanContinueOnboarding }
            : option,
        )
    : planOptions.filter(
        (option) =>
          option.value !== ClinicalImpressionPlanValueCode.DeclinedOnboarding,
      );

  const fields: (Field<FormData> | undefined)[] = [
    {
      subfields: [
        {
          key: 'locationCode',
          type: 'toggle-button',
          label: strings.SOAPNoteLocationDescription,
          options: availableLocationOptions,
        },
      ],
    },
    {
      subfields: [
        {
          key: 'code',
          type: 'combobox-single',
          label: strings.SOAPNoteCodeDescription,
          fetcher: availableCodeOptions,
        },
        // Only show practitioners to notify if not pre-onboarding
        ...(!isPlannedAdmission
          ? [
              {
                key: 'practitionersToNotify' as keyof FormData,
                type: 'combobox-multiple' as const,
                label: strings.FormLabelPractitionersToNotify,
                returnFullOption: false,
                fetcher: practitionerOptions,
              },
            ]
          : []),
      ],
    },
    {
      icon: 'text-s',
      subfields: [
        {
          key: 'subjective',
          type: 'text-box',
          label: '',
          placeholder: strings.SOAPNoteSubjectiveCommentsPlaceholder,
          multiline: true,
        },
      ],
    },
    // Only show vitals if not pre-onboarding
    ...(!isPlannedAdmission
      ? [
          {
            icon: 'text-o' as const,
            subfields: [
              {
                key: 'vitals' as keyof FormData,
                type: 'custom' as const,
                label: '',
                paddingHorizontal: false,
                Display: () => (
                  <StatusDisplay
                    latest={latest}
                    onAdd={handleObjectiveAdd}
                    showAdd={!showObjectiveNote}
                  />
                ),
              },
            ],
          },
        ]
      : []),
    showObjectiveNote
      ? {
          subfields: [
            {
              key: 'objective',
              type: 'text-box',
              label: '',
              placeholder: strings.SOAPNoteObjectiveCommentsPlaceholder,
              multiline: true,
            },
          ],
        }
      : undefined,
    {
      icon: 'text-a',
      subfields: [
        {
          key: 'assessmentCode',
          type: 'radio-group',
          label: '',
          options: availableAssessmentOptions,
          action: !showAssessmentNote
            ? {
                icon: 'add',
                onClick: handleAssessmentAdd,
              }
            : undefined,
        },
      ],
    },
    showAssessmentNote
      ? {
          subfields: [
            {
              key: 'assessment',
              type: 'text-box',
              label: '',
              placeholder: strings.SOAPNoteAssessmentCommentsPlaceholder,
              multiline: true,
            },
          ],
        }
      : undefined,
    {
      icon: 'text-p',
      subfields: [
        {
          key: 'planCode',
          type: 'radio-group',
          label: '',
          options: availablePlanOptions,
          action: !showPlanNote
            ? {
                icon: 'add',
                onClick: handlePlanAdd,
              }
            : undefined,
        },
      ],
    },
    showPlanNote
      ? {
          subfields: [
            {
              key: 'plan',
              type: 'text-box',
              label: '',
              placeholder: strings.SOAPNotePlanCommentsPlaceholder,
              multiline: true,
            },
          ],
        }
      : undefined,
    {
      subfields: [
        {
          key: 'media',
          type: 'file-picker-dialog',
          label: strings.FormLabelAttachments,
          buttonText: strings.ButtonTextAddAttachment,
          progress,
          uploadInProgress,
        },
      ],
    },
  ];

  return compact(fields);
};

export const initialValues: Partial<FormData> = {
  media: [],
  practitionersToNotify: [],
  code: ClinicalImpressionCode.Routine,
  locationCode: ClinicalImpressionLocationCode.Virtual,
};

export const initialValuesRestoreSchema = yup.object<ObjectType<FormData>>({
  media: yup.array(yup.object()).required(),
  practitionersToNotify: yup.array(yup.string()),
  code: yup.string().equals(Object.values(ClinicalImpressionCode)).required(),
  locationCode: yup
    .string()
    .equals(Object.values(ClinicalImpressionLocationCode))
    .required(),
});
